
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { BoPanel, IOnOffHandlers } from 'bonomo';
import BusinessClientNav from '@/components/businessClient/widget/BusinessClientNav.vue';
import { IBusinessClient } from '@/model/BusinessClient/businessClientInterfaces';
import { IErrors } from '@/model/IErrors';
import { IShortUser } from '@/model/shortInterfaces';
import axios from 'axios';
import { settings } from '@/plugins/settings';

const clientModule = namespace('clientModule');

@Component({
  name: 'BusinessClientEdit',
  components: { BoPanel, BusinessClientNav },
})
export default class BusinessClientEdit extends Vue {
  @Prop()
  protected clientId!: number;

  @clientModule.Getter('businessClient')
  protected client!: IBusinessClient;

  @clientModule.Getter('errors')
  protected errors!: IErrors;

  @clientModule.Action('fetchBusinessClient')
  private fetchClient!: (id: number) => Promise<any>;

  @clientModule.Action('updateBusinessClient')
  private updateClient!: (payload: { clientId: number; data: any }) => Promise<any>;

  protected loading: boolean = true;
  protected saving: boolean = false;
  private subscribes: IOnOffHandlers = {};
  private form: any = {};

  principalSearch: string = '';
  invoicingSearch: string = '';
  principalContactItems: IShortUser[] = [];
  invoicingContactItems: IShortUser[] = [];
  loadingPrincipal: boolean = false;
  loadingInvoicing: boolean = false;
  private principalTimer: any = null;
  private invoicingTimer: any = null;

  private rules = {
    required: (v: any) => !!v || this.$t('common.validation.required'),
    min2: (v: string) => (v && v.length >= 2) || this.$t('common.validation.min', { min: 2 }),
  };

  @Watch('principalSearch')
  onPrincipalSearch(val: string) {
    if (!val || val.length < 3) { this.principalContactItems = this.currentPrincipalItem(); return; }
    clearTimeout(this.principalTimer);
    this.principalTimer = setTimeout(() => this.searchUsers(val, 'principal'), 400);
  }

  @Watch('invoicingSearch')
  onInvoicingSearch(val: string) {
    if (!val || val.length < 3) { this.invoicingContactItems = this.currentInvoicingItem(); return; }
    clearTimeout(this.invoicingTimer);
    this.invoicingTimer = setTimeout(() => this.searchUsers(val, 'invoicing'), 400);
  }

  private currentPrincipalItem(): IShortUser[] {
    if (!this.client || !this.client.principal_contact) return [];
    return [{ id: this.client.principal_contact, email: this.client.principal_contact_email, first_name: this.client.principal_contact_first_name, last_name: this.client.principal_contact_last_name } as any];
  }

  private currentInvoicingItem(): IShortUser[] {
    if (!this.client || !this.client.invoicing_contact) return [];
    return [{ id: this.client.invoicing_contact, email: this.client.invoicing_contact_email, first_name: this.client.invoicing_contact_first_name, last_name: this.client.invoicing_contact_last_name } as any];
  }

  private async searchUsers(search: string, field: 'principal' | 'invoicing') {
    if (field === 'principal') this.loadingPrincipal = true;
    else this.loadingInvoicing = true;

    try {
      const response = await axios.get(settings.endpoint + 'users', { params: { search, active_only: 1 } });
      const payload = response.data && response.data.data;
      const items: IShortUser[] = Array.isArray(payload) ? payload : (Array.isArray(payload && payload.data) ? payload.data : []);
      if (field === 'principal') this.principalContactItems = items;
      else this.invoicingContactItems = items;
    } finally {
      if (field === 'principal') this.loadingPrincipal = false;
      else this.loadingInvoicing = false;
    }
  }

  created() {
    this.subscribes = this.$ons({
      'backend.call.OK.businessClientLoaded': this.initForm,
      'backend.call.begin.businessClientUpdated': () => { this.saving = true; },
      'backend.call.OK.businessClientUpdated': this.onSaveSuccess,
      'backend.call.KO.businessClientUpdated': () => { this.saving = false; },
    });
  }

  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  loadData() {
    this.fetchClient(this.clientId);
  }

  private initForm() {
    if (!this.client || !this.client.id) return;
    this.form = {
      name: this.client.name,
      legal_name: this.client.legal_name,
      principal_contact: this.client.principal_contact,
      invoicing_contact: this.client.invoicing_contact,
    };
    this.principalContactItems = this.currentPrincipalItem();
    this.invoicingContactItems = this.currentInvoicingItem();
  }

  private onSave() {
    this.updateClient({
      clientId: this.clientId,
      data: { ...this.form },
    });
  }

  private onSaveSuccess() {
    this.saving = false;
    this.$uiBus.emit('success', this.$t('common.saved'));
    this.$router.push({ name: 'businessClient', params: { clientId: '' + this.clientId } });
  }

  private onCancel() {
    this.$router.push({ name: 'businessClient', params: { clientId: '' + this.clientId } });
  }
}
